/**
 * 变换类 op —— 只构造 Command，不碰 DOM。
 *
 * 所有位置/尺寸/旋转改动都走同一入口 `applyWorldTransform`：
 * 世界矩阵 W 经"夹心乘法"共轭进元素父坐标系后，前置叠加到 transform 属性。
 * 因此无论元素在几层 <g> 里、自身是否带 rotate，结果都正确。
 */
import { pathOf } from '../dom';
import { IDENTITY, isFiniteMat, isIdentityMat, translate, type Mat, type Rect } from '../geom/matrix';
import { nextTransformAttribute, snapValue } from '../geom/handles';
import type { Command } from '../history';
import { attrPatch, type Patch } from '../patch';

export interface TransformDeps {
  root: Element;
  /** 元素「父坐标系 → 根用户坐标」的矩阵；无真实矩阵的环境（jsdom 测试）返回 IDENTITY */
  parentMatOf(el: Element): Mat;
}

export const identityDeps = (root: Element): TransformDeps => ({ root, parentMatOf: () => IDENTITY });

/** 世界矩阵 → 各元素 transform 属性 patch */
export function applyWorldTransform(
  deps: TransformDeps,
  els: Iterable<Element>,
  world: Mat,
  label: string,
  mergeKey?: string,
): Command {
  const patches: Patch[] = [];
  if (!isFiniteMat(world) || isIdentityMat(world)) return { label, patches, mergeKey };
  for (const el of els) {
    const path = pathOf(el, deps.root);
    if (!path) continue;
    const before = el.getAttribute('transform');
    const after = nextTransformAttribute(before, world, deps.parentMatOf(el));
    const p = attrPatch(path, 'transform', before, after);
    if (p) patches.push(p);
  }
  return { label, patches, mergeKey };
}

export interface MoveOptions {
  mergeKey?: string;
  /** 网格吸附：把**结果位置**吸附到格点（不是吸附位移，避免连续拖动累积漂移） */
  grid?: number;
  /** 选择框（根用户坐标），提供时按框的原点吸附 */
  box?: Rect;
}

export function moveBy(
  deps: TransformDeps,
  els: Iterable<Element>,
  dx: number,
  dy: number,
  opts: MoveOptions = {},
): Command {
  let ddx = dx;
  let ddy = dy;
  if (opts.grid && opts.grid > 0) {
    if (opts.box) {
      ddx = snapValue(opts.box.x + dx, opts.grid) - opts.box.x;
      ddy = snapValue(opts.box.y + dy, opts.grid) - opts.box.y;
    } else {
      ddx = snapValue(dx, opts.grid);
      ddy = snapValue(dy, opts.grid);
    }
  }
  if (!Number.isFinite(ddx) || !Number.isFinite(ddy) || (ddx === 0 && ddy === 0)) {
    return { label: '移动', patches: [], mergeKey: opts.mergeKey };
  }
  return applyWorldTransform(deps, els, translate(ddx, ddy), '移动', opts.mergeKey);
}

/** 每个元素各自的位移（对齐/分布用；一条命令里装 N 个 patch） */
export function moveEachBy(
  deps: TransformDeps,
  els: Element[],
  offsets: ReadonlyArray<{ x: number; y: number }>,
  label: string,
): Command {
  const patches: Patch[] = [];
  els.forEach((el, i) => {
    const off = offsets[i];
    if (!off) return;
    patches.push(...moveBy(deps, [el], off.x, off.y).patches);
  });
  return { label, patches };
}

/** 方向键微移（连续按键合并成一条 undo） */
export const nudgeBy = (deps: TransformDeps, els: Iterable<Element>, dx: number, dy: number): Command =>
  moveBy(deps, els, dx, dy, { mergeKey: 'nudge' });

/** 手柄缩放 / 旋转：已经算好的世界矩阵直接套用 */
export const resizeBy = (deps: TransformDeps, els: Iterable<Element>, world: Mat): Command =>
  applyWorldTransform(deps, els, world, '缩放', 'resize');

export const rotateBy = (deps: TransformDeps, els: Iterable<Element>, world: Mat): Command =>
  applyWorldTransform(deps, els, world, '旋转', 'rotate');
